import { vec3 } from 'gl-matrix';
import { Graph, PathFinder } from '../path-finding';
import { PathNode } from './PathNode';

/**
 * Default expansion cap for the voxel-level optimizer. Higher than the
 * coarse A* cap because the optimizer searches at voxel resolution and
 * legitimate paths can be many hundreds of voxels long.
 */
export const DEFAULT_OPTIMIZER_MAX_EXPANSIONS = 20000;

/** Width of the section-cache key range — supports node bboxes up to ~50k voxels wide. */
const SECTION_KEY_STRIDE = 1_000_003;

/**
 * One step in an optimized movement path: a position to walk toward and the
 * coarse `PathNode` it falls inside. The runtime ignores the segment's Y
 * (`NavigationAwareObject.onStep` resolves true floor heights live), so the
 * optimizer only does XZ work.
 */
export type PathSegment = {
  position: vec3 | number[];
  node: PathNode;
};

/**
 * Voxel-level fine-grained pathfinder. Walks a coarse `PathNode[]` corridor
 * one voxel at a time on the XZ plane and reduces the result to one
 * `PathSegment` per coarse node — the last voxel visited inside that node.
 *
 * Drive it via `optimize()`. The lower-level `getSectionNode` /
 * `getSectionNodeAt` / A* hooks are public so the algorithm stays composable,
 * but the typical caller only needs `optimize()`.
 */
export class PathOptimizer extends PathFinder<PathSectionNode> implements Graph<PathSectionNode> {
  /**
   * Section node cache, scoped per coarse path node so different runs
   * of the optimizer can't accidentally share section nodes from a
   * different corridor. Cleared on every `reset()`.
   */
  private map: Map<PathNode, Map<number, PathSectionNode>> = new Map();

  /** Pre-allocated neighbour offsets [dx, dz, dx, dz, dx, dz, dx, dz]. */
  public readonly neighbourOffsets: number[];

  public voxelSize: number;

  constructor(voxelSize: number = 1) {
    super();
    this.setGraph(this);

    this.voxelSize = voxelSize;
    this.neighbourOffsets = [voxelSize, 0, 0, voxelSize, -voxelSize, 0, 0, -voxelSize];

    // Manhattan XZ heuristic. With 4-connected grid neighbours and a per-
    // step cost of voxelSize, Manhattan distance is the *exact* optimal
    // cost — A* is consistent and expands close to the minimum number of
    // voxels. Y is ignored (held constant per node) since the runtime
    // resolves true floor heights in NavigationAwareObject.onStep.
    this.setHeuristic((node: PathSectionNode) => {
      const dx = node.location[0] - this.targetPosition[0];
      const dz = node.location[2] - this.targetPosition[2];
      return Math.abs(dx) + Math.abs(dz);
    });
  }

  /**
   * Run the full voxel-level optimization for a coarse path. Owns the
   * driver loop end-to-end: linked-list build, cache reset, start/goal
   * section construction, A*, result reduction.
   *
   * Returns one `PathSegment` per coarse `PathNode` in the optimized walk
   * (the last voxel visited in that node). Returns an empty array on
   * failure (e.g. expansion cap hit) and logs a warning.
   */
  public optimize(
    currentNode: PathNode,
    coarsePath: PathNode[],
    originPosition: vec3 | number[],
    targetPosition: vec3 | number[],
    maxExpansions: number = DEFAULT_OPTIMIZER_MAX_EXPANSIONS,
  ): PathSegment[] {
    // Trivial case: the entity is already in the goal node. Walk straight
    // to the target without running A*.
    if (coarsePath.length === 1 && coarsePath[0] === currentNode) {
      return [{ node: currentNode, position: targetPosition }];
    }

    const walkPath = [currentNode, ...coarsePath];
    const { head, tail } = createLinkedNodes(walkPath);

    this.reset();

    const start = this.getSectionNodeAt(head, originPosition);
    const goal = this.getSectionNodeAt(tail!, targetPosition);

    const optimizedPath = this.findPath(start, goal, originPosition, targetPosition, {
      maxExpansions,
    });

    if (!optimizedPath.solved) {
      if (optimizedPath.exhausted) {
        console.warn(
          `Path optimizer hit expansion cap (${maxExpansions}); ` +
            `path target unreachable at voxel resolution.`,
        );
      } else {
        console.warn('No solution found');
      }
      return [];
    }

    // Reduce the voxel-level path to one entry per unique coarse node:
    // the *last* voxel visited inside that node. Map preserves insertion
    // order, so iterating its values gives segments in walk order.
    const perNode = new Map<PathNode, vec3 | number[]>();
    for (const step of optimizedPath.path) {
      perNode.set(step.linkedNode.node, step.location);
    }

    const result: PathSegment[] = [];
    perNode.forEach((position, node) => {
      result.push({ position, node });
    });
    return result;
  }

  /** Drop the section cache so the next pathfind starts clean. */
  public reset(): void {
    this.map.clear();
  }

  /**
   * Look up (or create) the section node for the given linked path node and
   * raw world XZ coordinates. Y is held constant at the path node's min Y
   * because the runtime resolves true floor heights per movement step in
   * `NavigationAwareObject.onStep` — any Y the optimizer computes is thrown
   * away. Skipping the floor query is the dominant perf win here, since
   * `spatialGrid.getFloorHeightBelow` walks the volgrid quadtree.
   */
  public getSectionNode(linkedNode: LinkedPathNode, x: number, z: number): PathSectionNode {
    let inner = this.map.get(linkedNode.node);
    if (!inner) {
      inner = new Map<number, PathSectionNode>();
      this.map.set(linkedNode.node, inner);
    }

    const sx = Math.round(x / this.voxelSize);
    const sz = Math.round(z / this.voxelSize);
    // Numeric key. SECTION_KEY_STRIDE >> any plausible voxel index, so
    // (sx, sz) pairs are uniquely encoded.
    const key = sx * SECTION_KEY_STRIDE + sz;

    const cached = inner.get(key);
    if (cached) return cached;

    const section = new PathSectionNode(linkedNode, [
      sx * this.voxelSize,
      linkedNode.node.min[1],
      sz * this.voxelSize,
    ]);
    inner.set(key, section);
    return section;
  }

  /**
   * Convenience entry that takes a raw world `vec3` (or `number[]`) and
   * forwards XZ to `getSectionNode`. Y is discarded.
   */
  public getSectionNodeAt(linkedNode: LinkedPathNode, location: vec3 | number[]): PathSectionNode {
    return this.getSectionNode(linkedNode, location[0], location[2]);
  }

  getCost(_a: PathSectionNode, _b: PathSectionNode): number {
    // 4-connected grid: every step covers exactly voxelSize world units.
    // Matching units to the Manhattan heuristic makes A* consistent and
    // exploration ~minimal.
    return this.voxelSize;
  }

  getNeighbours(element: PathSectionNode): PathSectionNode[] {
    return element.getNeighbours(this);
  }
}

/**
 * Forward-linked list node used to thread coarse `PathNode`s for the
 * optimizer. `next` lets a section node fall through into the following
 * coarse node when its voxel walk crosses a corridor boundary.
 */
class LinkedPathNode {
  constructor(
    public node: PathNode,
    public next?: LinkedPathNode,
  ) {}
}

/** A single voxel inside a coarse path node, with neighbour expansion. */
class PathSectionNode {
  constructor(
    public linkedNode: LinkedPathNode,
    public location: vec3 | number[],
  ) {}

  getNeighbours(optimizer: PathOptimizer): PathSectionNode[] {
    // Y-up: step on the XZ ground plane using voxelSize as step quantum.
    // Offsets are pre-allocated on the optimizer to avoid per-call alloc.
    const offsets = optimizer.neighbourOffsets;
    const x0 = this.location[0];
    const z0 = this.location[2];
    const node = this.linkedNode.node;
    const nextLinked = this.linkedNode.next;
    const nextNode = nextLinked?.node;

    const neighbours: PathSectionNode[] = [];

    for (let i = 0; i < 4; i++) {
      const nx = x0 + offsets[i * 2];
      const nz = z0 + offsets[i * 2 + 1];

      // XZ containment is the only filter — Y is constant per node so
      // there's no floor query needed (see PathOptimizer.getSectionNode).
      let containingLinked: LinkedPathNode | undefined;
      if (containsXZ(node, nx, nz)) {
        containingLinked = this.linkedNode;
      } else if (nextNode && containsXZ(nextNode, nx, nz)) {
        containingLinked = nextLinked;
      }
      if (!containingLinked) continue;

      neighbours.push(optimizer.getSectionNode(containingLinked, nx, nz));
    }

    return neighbours;
  }
}

function containsXZ(node: PathNode, x: number, z: number): boolean {
  return x >= node.min[0] && x <= node.max[0] && z >= node.min[2] && z <= node.max[2];
}

/**
 * Build a forward linked list from a coarse path. Recursive to keep the
 * tail handy without a second pass. Internal — only `optimize()` calls it.
 */
function createLinkedNodes(path: PathNode[]): {
  head: LinkedPathNode;
  tail: LinkedPathNode | undefined;
} {
  let tail: LinkedPathNode | undefined = undefined;
  const remaining = path.slice();
  const link = (): LinkedPathNode => {
    if (remaining.length > 1) {
      const current = remaining.shift()!;
      return new LinkedPathNode(current, link());
    } else {
      tail = new LinkedPathNode(remaining.shift()!, undefined);
      return tail;
    }
  };
  const head = link();
  return { head, tail };
}
